import { createHHAxios } from '@/references/axios/axios';
import {
  type BankTransferRecipient,
  BankTransferRecipientType,
  type CheckIBANResponse
} from '@/references/axios/bankTransfer/types';
import { HHAPIService } from '@/references/axios/base';
import type { ClientType } from '@/references/base';

export class HHAPIBankTransferService extends HHAPIService {
  constructor(
    private baseURL: string,
    private clientType: ClientType
  ) {
    super('bt.service');
  }

  public async checkIBANRequest(iban: string): Promise<CheckIBANResponse> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    return (
      await instance.request<CheckIBANResponse, { iban: string }>({
        method: 'POST',
        url: '/v1/private/iban/get-data',
        data: {
          iban
        },
        withCredentials: true
      })
    ).data.payload;
  }

  public async getBankTransferRecipients(): Promise<BankTransferRecipient[]> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    return (
      await instance.request<{ recipients: BankTransferRecipient[] }>({
        method: 'GET',
        url: '/v1/private/sepa/recipients',
        withCredentials: true
      })
    ).data.payload.recipients;
  }

  public async createBankTransferRecipient(
    type: BankTransferRecipientType,
    firstName: string,
    lastName: string,
    nickname: string,
    iban: string,
    bic: string,
    bankName: string
  ): Promise<BankTransferRecipient[]> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    return (
      await instance.request<
        { recipients: BankTransferRecipient[] },
        {
          type: BankTransferRecipientType;
          firstName: string;
          lastName: string;
          nickname: string;
          iban: string;
          bic: string;
          bankName: string;
        }
      >({
        method: 'POST',
        url: '/v1/private/sepa/recipient/add',
        data: {
          type,
          firstName,
          lastName,
          nickname,
          iban,
          bic,
          bankName
        },
        withCredentials: true
      })
    ).data.payload.recipients;
  }

  public async updateBankTransferRecipient(
    id: number,
    type: BankTransferRecipientType,
    firstName: string,
    lastName: string,
    nickname: string,
    iban: string,
    bic: string,
    bankName: string
  ): Promise<BankTransferRecipient[]> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    return (
      await instance.request<
        { recipients: BankTransferRecipient[] },
        {
          id: number;
          type: BankTransferRecipientType;
          firstName: string;
          lastName: string;
          nickname: string;
          iban: string;
          bic: string;
          bankName: string;
        }
      >({
        method: 'POST',
        url: '/v1/private/sepa/recipient/update',
        data: {
          id,
          type,
          firstName,
          lastName,
          nickname,
          iban,
          bic,
          bankName
        },
        withCredentials: true
      })
    ).data.payload.recipients;
  }

  public async removeBankTransferRecipient(id: number): Promise<BankTransferRecipient[]> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    return (
      await instance.request<{ recipients: BankTransferRecipient[] }, { id: number }>({
        method: 'POST',
        url: '/v1/private/sepa/recipient/remove',
        data: {
          id
        },
        withCredentials: true
      })
    ).data.payload.recipients;
  }
}
